/**
 * Variant Quick Add Overlay
 * Replaces the standard quick-add modal for products with multiple
 * variants: clicking "Choose options" opens an on-card overlay listing
 * each variant (option1) + price; clicking a row adds it to the cart
 * immediately, then refreshes/opens the cart drawer.
 *
 * ASSUMPTIONS TO VERIFY AGAINST YOUR THEME:
 * - `window.routes.cart_add_url` is defined globally (Dawn sets this
 *   in theme.liquid by default — confirm it's still present).
 * - Your cart drawer section's Liquid `{{ section.id }}` resolves to
 *   "cart-drawer". If your cart-drawer.liquid section has a different
 *   handle/id, update CART_DRAWER_SECTION_ID below to match.
 * - Your <cart-drawer> custom element (if present) exposes an
 *   `.open()` method, matching Dawn's default cart-drawer.js.
 */

(function () {
  if (window.variantQuickAddInitialized) return;
  window.variantQuickAddInitialized = true;

  var CART_DRAWER_SECTION_ID = 'cart-drawer';
  var CART_ICON_SECTION_ID = 'cart-icon-bubble';

  function closeOverlay(overlay, toggleBtn) {
    overlay.hidden = true;

    if (toggleBtn) {
      toggleBtn.setAttribute('aria-expanded', 'false');
      toggleBtn.classList.remove('variant-open');

      var label = toggleBtn.querySelector('.variant-overlay-toggle__label');
      if (label && label.dataset.defaultText) {
        label.textContent = label.dataset.defaultText;
      }
    }
  }

  function openOverlay(overlay, toggleBtn) {
    // Close any other open overlays first
    document.querySelectorAll('.variant-overlay:not([hidden])').forEach(function (openEl) {
      if (openEl !== overlay) {
        var otherToggle = document.querySelector('[aria-controls="' + openEl.id + '"]');
        closeOverlay(openEl, otherToggle);
      }
    });

    overlay.hidden = false;

    if (toggleBtn) {
      toggleBtn.setAttribute('aria-expanded', 'true');
      toggleBtn.classList.add('variant-open');

      var label = toggleBtn.querySelector('.variant-overlay-toggle__label');
      if (label) {
        if (!label.dataset.defaultText) {
          label.dataset.defaultText = label.textContent;
        }
        label.textContent = 'Add to basket';
      }
    }
  }

  function refreshCartDrawer(cartState) {
    var cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && cartState && cartState.sections && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(cartState);
      cartDrawer.classList.remove('is-empty');
      return Promise.resolve();
    }

    var cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    return fetch(cartUrl + '?section_id=' + CART_DRAWER_SECTION_ID)
      .then(function (response) {
        if (!response.ok) throw new Error('Failed to fetch cart drawer section');
        return response.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newDrawer = doc.querySelector('cart-drawer');
        var currentDrawer = document.querySelector('cart-drawer');

        if (newDrawer && currentDrawer) {
          currentDrawer.innerHTML = newDrawer.innerHTML;
        }

        // Cart icon bubble (item count) — separate small section, fetched
        // together where possible to avoid an extra request if your setup
        // already includes it in the cart-drawer markup.
        return fetch(cartUrl + '?section_id=' + CART_ICON_SECTION_ID);
      })
      .then(function (response) {
        if (!response || !response.ok) return null;
        return response.text();
      })
      .then(function (html) {
        if (!html) return;
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var newBubble = doc.querySelector('.cart-icon-bubble') || doc.body.firstElementChild;
        var currentBubble = document.querySelector('.cart-icon-bubble') || document.querySelector('#cart-icon-bubble');
        if (newBubble && currentBubble) {
          currentBubble.innerHTML = newBubble.innerHTML;
        }
      })
      .then(function () {
        var cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.open();
        }
      })
      .catch(function (error) {
        console.error('[variant-quick-add] Cart drawer refresh failed:', error);
      });
  }

  function addVariantToCart(row) {
    var variantId = row.dataset.variantId;
    if (!variantId || row.classList.contains('is-loading')) return;

    var cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    var cartDrawer = document.querySelector('cart-drawer');
    var requestBody = { id: variantId, quantity: 1 };

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      requestBody.sections = cartDrawer.getSectionsToRender().map(function (section) {
        return section.id;
      });
      requestBody.sections_url = window.location.pathname;
      if (typeof cartDrawer.setActiveElement === 'function') {
        cartDrawer.setActiveElement(row);
      }
    }

    row.classList.remove('is-error');
    row.classList.add('is-loading');

    fetch(cartAddUrl + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(requestBody)
    })
      .then(function (response) {
        if (!response.ok) {
          return response.json().then(function (err) {
            throw new Error(err.description || 'Add to cart failed');
          });
        }
        return response.json();
      })
      .then(function (cartState) {
        if (cartState.status) {
          throw new Error(cartState.description || cartState.message || 'Add to cart failed');
        }

        var overlay = row.closest('.variant-overlay');
        var toggleBtn = overlay ? document.querySelector('[aria-controls="' + overlay.id + '"]') : null;
        if (overlay) closeOverlay(overlay, toggleBtn);

        return refreshCartDrawer(cartState);
      })
      .catch(function (error) {
        console.error('[variant-quick-add] Add to cart error:', error);
        row.classList.add('is-error');
      })
      .finally(function () {
        row.classList.remove('is-loading');
      });
  }

  function initVariantQuickAdd() {
    document.addEventListener('click', function (event) {
      var toggleBtn = event.target.closest('.variant-overlay-toggle');
      if (toggleBtn) {
        var overlayId = toggleBtn.getAttribute('aria-controls');
        var overlay = overlayId ? document.getElementById(overlayId) : null;
        if (!overlay) return;

        if (overlay.hidden) {
          openOverlay(overlay, toggleBtn);
        } else {
          closeOverlay(overlay, toggleBtn);
        }
        return;
      }

      var closeBtn = event.target.closest('.variant-overlay__close');
      if (closeBtn) {
        var overlayToClose = closeBtn.closest('.variant-overlay');
        var relatedToggle = overlayToClose
          ? document.querySelector('[aria-controls="' + overlayToClose.id + '"]')
          : null;
        if (overlayToClose) closeOverlay(overlayToClose, relatedToggle);
        return;
      }

      var row = event.target.closest('.variant-overlay__row');
      if (row) {
        addVariantToCart(row);
        return;
      }

      if (!event.target.closest('.variant-overlay')) {
        document.querySelectorAll('.variant-overlay:not([hidden])').forEach(function (openEl) {
          var relatedToggle = document.querySelector('[aria-controls="' + openEl.id + '"]');
          closeOverlay(openEl, relatedToggle);
        });
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      document.querySelectorAll('.variant-overlay:not([hidden])').forEach(function (openEl) {
        var relatedToggle = document.querySelector('[aria-controls="' + openEl.id + '"]');
        closeOverlay(openEl, relatedToggle);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVariantQuickAdd);
  } else {
    initVariantQuickAdd();
  }
})();
